import React from 'react';
import {
  PrinterIcon,
  XMarkIcon,
  UserIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useGenerateSalarySlipQuery } from '../../services/payrollApi';
import { useGetMyCompanyQuery } from '../../services/companyApi';

const SalarySlipDialog = ({ open, onClose, payrollId, employeeId }) => {
  const { data, isLoading, error } = useGenerateSalarySlipQuery(
    { payrollId, employeeId },
    { skip: !payrollId || !employeeId }
  );
  const { data: companyData } = useGetMyCompanyQuery();
  const company = companyData?.data;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount || 0);
  };

  const numberToWords = (num) => {
    if (num === 0 || num === null || num === undefined) return 'Zero';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const toWordsBelowThousand = (n) => {
      let str = '';
      if (n >= 100) {
        str += ones[Math.floor(n / 100)] + ' Hundred ';
        n = n % 100;
      }
      if (n >= 20) {
        str += tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      } else if (n > 0) {
        str += ones[n];
      }
      return str.trim();
    };
    const parts = [
      { value: 10000000, name: 'Crore' },
      { value: 100000, name: 'Lakh' },
      { value: 1000, name: 'Thousand' },
      { value: 100, name: 'Hundred' },
    ];
    let n = Math.floor(Math.abs(num));
    let words = '';
    for (const p of parts) {
      if (n >= p.value) {
        const q = Math.floor(n / p.value);
        if (p.value === 100) {
          words += ones[q] + ' ' + p.name + ' ';
        } else {
          words += toWordsBelowThousand(q) + ' ' + p.name + ' ';
        }
        n = n % p.value;
      }
    }
    words += toWordsBelowThousand(n);
    return words.trim() || 'Zero';
  };

const buildPrintableHtml = (payroll, companyInfo) => {
  const fmt = (v) => formatCurrency(v || 0);
  const safe = (v, d = '-') => (v === null || v === undefined || v === '' ? d : String(v));
  const joinAddress = (addr) => {
    if (!addr) return '';
    const parts = [addr.street, addr.city, addr.state, addr.zipCode, addr.country].filter(Boolean);
    return parts.join(', ');
  };
  const monthName =
    typeof payroll.month === 'number'
      ? new Date(2000, payroll.month - 1, 1).toLocaleString('en-IN', { month: 'long' })
      : safe(payroll.month);
  const period = `${monthName} ${safe(payroll.year, '')}`.trim();

  const gross =
    payroll.grossEarnings ??
    ((payroll.basicSalary || 0) +
      (payroll.hra || 0) +
      (Array.isArray(payroll.allowances)
        ? payroll.allowances.reduce((s, a) => s + (a?.amount || 0), 0)
        : 0));
  const deductions =
    payroll.totalDeductions ??
    ((payroll.pfEmployee || 0) +
      (payroll.esiEmployee || 0) +
      (payroll.professionalTax || 0) +
      (payroll.tds || 0) +
      (payroll.loanDeduction || 0));
  const net = payroll.netSalary ?? (gross - deductions);
  const netRupees = Math.floor(net || 0);
  const netPaise = Math.round(((net || 0) - netRupees) * 100);
  const netInWords = `${numberToWords(netRupees)} Rupees${
    netPaise ? ' and ' + numberToWords(netPaise) + ' Paise' : ''
  } Only`;

  const allowancesRows = (Array.isArray(payroll.allowances) ? payroll.allowances : [])
    .map((a) => `<tr><td>${safe(a?.name)}</td><td class="amt">${fmt(a?.amount)}</td></tr>`)
    .join('');

  const bankName = payroll.bankName || payroll.bankDetails?.bankName;
  const bankAcc = payroll.bankAccountNumber || payroll.bankDetails?.accountNumber || payroll.accountNumber;
  const ifsc = payroll.ifsc || payroll.bankDetails?.ifsc;
  const branchName = payroll.branchName || payroll.bankDetails?.branchName;
  const accountHolderName = payroll.accountHolderName || payroll.bankDetails?.accountHolderName || payroll.employeeName;
  const pan = payroll.pan || payroll.panNumber;
  const uan = payroll.uanNumber || payroll.uan;
  const esic = payroll.esiNumber || payroll.esic;
  const pfNumber = payroll.pfNumber || payroll.pf;

  const companyName = safe(companyInfo?.name || payroll.companyName, 'Company');
  const companyAddress = companyInfo?.address ? joinAddress(companyInfo.address) : safe(payroll.companyAddress, '');
  const companyContact = [companyInfo?.phone, companyInfo?.email, companyInfo?.website].filter(Boolean).join(' • ');
  const logoText = (companyName || 'SS')
    .split(/\s+/)
    .map((w) => w && w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Salary Slip - ${safe(payroll.employeeName)} - ${period}</title>
  <style>
    :root {
      --primary: #1e40af;
      --secondary: #475569;
      --accent: #f8fafc;
      --border: #e2e8f0;
      --text: #0f172a;
      --muted: #64748b;
      --success: #059669;
      --background: #ffffff;
    }

    * { box-sizing: border-box; margin: 0; padding: 0; }

    html, body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 14px;
      line-height: 1.4;
      color: var(--text);
      background: var(--background);
    }

    .container {
      width: 210mm;
      min-height: 297mm;
      margin: auto;
      padding: 15mm;
      position: relative;
    }

    /* Watermark */
    .container::before {
      content: "${companyName}";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-30deg);
      font-size: 60px;
      font-weight: 800;
      color: rgba(0,0,0,0.05);
      pointer-events: none;
      white-space: nowrap;
    }

    .header {
      background: var(--primary);
      color: white;
      padding: 12px 15px;
      border-radius: 6px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }

    .company-info { display: flex; align-items: center; gap: 10px; }
    .logo {
      width: 40px; height: 40px; border-radius: 6px;
      background: rgba(255,255,255,0.2);
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 14px;
    }
    .company-details h1 { font-size: 16px; margin-bottom: 2px; }
    .company-details p { font-size: 11px; opacity: 0.9; }

    .document-info { text-align: right; }
    .document-info .title { font-size: 15px; font-weight: 700; }
    .document-info .period { font-size: 12px; opacity: 0.9; }

    .section { border: 1px solid var(--border); border-radius: 6px; padding: 10px; margin-bottom: 10px; }
    .section-title {
      font-size: 12px; font-weight: 700; color: var(--primary);
      margin-bottom: 8px; border-bottom: 1px solid var(--border); padding-bottom: 3px;
    }

    .employee-info { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
    .info-item { font-size: 12px; display: flex; justify-content: space-between; }
    .info-label { color: var(--muted); }
    .info-value { font-weight: 600; }

    .salary-section { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th, td { padding: 6px 8px; text-align: left; border-bottom: 1px solid var(--border); }
    th { background: var(--accent); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
    td.amt { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; }

    tfoot th, tfoot td { font-weight: 700; color: var(--primary); }

    .net-salary {
      background: var(--success);
      color: white;
      text-align: center;
      padding: 12px;
      border-radius: 6px;
      margin: 12px 0;
    }
    .net-amount { font-size: 20px; font-weight: 800; }
    .net-words { font-size: 11px; font-style: italic; opacity: 0.9; }

    .loan-detail {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px;
      background: #f3f4f6;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      margin-bottom: 4px;
    }
    .loan-info {
      display: flex;
      flex-direction: column;
    }
    .loan-id {
      font-size: 11px;
      font-weight: 600;
      color: #7c3aed;
    }
    .installment {
      font-size: 10px;
      color: #6b7280;
    }
    .loan-amount {
      text-align: right;
    }
    .amount {
      font-size: 12px;
      font-weight: 700;
      color: #1f2937;
      display: block;
    }
    .due-date {
      font-size: 10px;
      color: #6b7280;
    }
    .remaining-balance {
      font-size: 10px;
      color: #6b7280;
      font-weight: 600;
    }

    .signature { text-align: center; margin-top: 20px; }
    .signature-box {
      border: 1px solid var(--border);
      height: 40px;
      margin-bottom: 5px;
      border-radius: 4px;
    }
    .signature-text { font-size: 11px; font-weight: 600; }

    .footer {
      margin-top: 15px;
      font-size: 10px;
      color: var(--muted);
      display: flex;
      justify-content: space-between;
      border-top: 1px solid var(--border);
      padding-top: 8px;
    }

    @page { size: A4; margin: 10mm; }
    @media print {
      body { background: white !important; -webkit-print-color-adjust: exact; }
      .container::before { opacity: 0.08; }
    }
  </style>
</head>
<body onload="window.print();" onafterprint="setTimeout(() => window.close(), 300)">
  <div class="container">
    <div class="header">
      <div class="company-info">
        <div class="logo">${logoText}</div>
        <div class="company-details">
          <h1>${companyName}</h1>
          <p>${companyAddress}</p>
          ${companyContact ? `<p>${companyContact}</p>` : ''}
        </div>
      </div>
      <div class="document-info">
        <div class="title">Salary Slip</div>
        <div class="period">${period}</div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Employee Information</div>
      <div class="employee-info">
        <div class="info-item"><span class="info-label">Employee Name</span><span class="info-value">${safe(payroll.employeeName)}</span></div>
        <div class="info-item"><span class="info-label">Employee ID</span><span class="info-value">${safe(payroll.employeeId?.employeeId)}</span></div>
        <div class="info-item"><span class="info-label">Department</span><span class="info-value">${safe(payroll.department)}</span></div>
        <div class="info-item"><span class="info-label">Designation</span><span class="info-value">${safe(payroll.designation)}</span></div>
        <div class="info-item"><span class="info-label">Working Days</span><span class="info-value">${safe(payroll.totalWorkingDays)}</span></div>
        <div class="info-item"><span class="info-label">Present Days</span><span class="info-value">${safe(payroll.presentDays)}</span></div>
      </div>
    </div>

    <div class="salary-section">
      <div class="section">
        <div class="section-title">Earnings</div>
        <table>
          <thead><tr><th>Description</th><th class="amt">Amount (₹)</th></tr></thead>
          <tbody>
            <tr><td>Basic Salary</td><td class="amt">${fmt(payroll.basicSalary)}</td></tr>
            <tr><td>HRA</td><td class="amt">${fmt(payroll.hra)}</td></tr>
            ${allowancesRows}
          </tbody>
          <tfoot><tr><th>Total Earnings</th><th class="amt">${fmt(gross)}</th></tr></tfoot>
        </table>
      </div>

      <div class="section">
        <div class="section-title">Deductions</div>
        <table>
          <thead><tr><th>Description</th><th class="amt">Amount (₹)</th></tr></thead>
          <tbody>
            <tr><td>PF Employee</td><td class="amt">${fmt(payroll.pfEmployee)}</td></tr>
            <tr><td>ESI Employee</td><td class="amt">${fmt(payroll.esiEmployee)}</td></tr>
            <tr><td>Professional Tax</td><td class="amt">${fmt(payroll.professionalTax)}</td></tr>
            <tr><td>TDS</td><td class="amt">${fmt(payroll.tds)}</td></tr>
            ${payroll.loanDeduction > 0 ? `<tr><td>Loan Deduction</td><td class="amt">${fmt(payroll.loanDeduction)}</td></tr>` : ''}
          </tbody>
          <tfoot><tr><th>Total Deductions</th><th class="amt">${fmt(deductions)}</th></tr></tfoot>
        </table>
      </div>
    </div>

    <div class="net-salary">
      <div class="net-label">Net Salary</div>
      <div class="net-amount">${fmt(net)}</div>
      <div class="net-words">${netInWords}</div>
    </div>

    ${payroll.loanDeductionDetails && payroll.loanDeductionDetails.length > 0 ? `
    <div class="section">
      <div class="section-title">Loan Repayment Details</div>
      <div class="space-y-2">
        ${payroll.loanDeductionDetails.map(detail => `
          <div class="loan-detail">
            <div class="loan-info">
              <span class="loan-id">Loan ID: ${detail.loanApplicationId}</span>
              <span class="installment">Installment ${detail.installmentNumber}</span>
            </div>
            <div class="loan-amount">
              <span class="amount">${fmt(detail.amount)}</span>
              <span class="due-date">Due: ${new Date(detail.dueDate).toLocaleDateString('en-GB')}</span>
              <span class="remaining-balance">Remaining: ${fmt(detail.remainingBalance)}</span>
            </div>
          </div>
        `).join('')}
      </div>
    </div>
    ` : ''}

    <div class="section">
      <div class="section-title">Bank & Statutory Information</div>
      <div class="employee-info">
        <div class="info-item"><span class="info-label">Bank Name</span><span class="info-value">${safe(bankName)}</span></div>
        <div class="info-item"><span class="info-label">Account No.</span><span class="info-value">${safe(bankAcc)}</span></div>
        <div class="info-item"><span class="info-label">IFSC Code</span><span class="info-value">${safe(ifsc)}</span></div>
        <div class="info-item"><span class="info-label">Branch Name</span><span class="info-value">${safe(branchName)}</span></div>
        <div class="info-item"><span class="info-label">Account Holder</span><span class="info-value">${safe(accountHolderName)}</span></div>
        <div class="info-item"><span class="info-label">PAN</span><span class="info-value">${safe(pan)}</span></div>
        <div class="info-item"><span class="info-label">UAN</span><span class="info-value">${safe(uan)}</span></div>
        <div class="info-item"><span class="info-label">ESIC</span><span class="info-value">${safe(esic)}</span></div>
        <div class="info-item"><span class="info-label">PF Number</span><span class="info-value">${safe(pfNumber)}</span></div>
        <div class="info-item"><span class="info-label">Tax Regime</span><span class="info-value">${safe(payroll.taxRegime)?.toUpperCase() || '-'}</span></div>
        <div class="info-item"><span class="info-label">Taxable Income</span><span class="info-value">${fmt(payroll.taxableIncome)}</span></div>
        <div class="info-item"><span class="info-label">TDS Applicable</span><span class="info-value">${payroll.tdsApplicable ? 'Yes' : 'No'}</span></div>
      </div>
    </div>

    <div class="signature">
      <div class="signature-box"></div>
      <div class="signature-text">Authorised Signatory</div>
    </div>

    <div class="footer">
      <div>This is a system generated document and does not require signature.</div>
      <div>Generated on ${new Date().toLocaleDateString('en-IN')}</div>
    </div>
  </div>
</body>
</html>`;
};

  const handlePrint = () => {
    const payroll = data?.data?.payroll;
    const backendHtml = data?.data?.html;
    if (!payroll && !backendHtml) {
      console.error('No salary slip data available for printing');
      return;
    }

  const html = payroll ? buildPrintableHtml(payroll, company) : backendHtml;

    const printWindow = window.open('', '_blank', 'width=1000,height=800');
    if (!printWindow) {
      alert('Please allow popups for this website to print salary slips');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header - Fixed height */}
        <div className="flex-shrink-0 p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center">
                <DocumentTextIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Salary Slip</h2>
                <p className="text-gray-600">Employee salary details and breakdown</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handlePrint}
                disabled={!data?.data?.payroll && !data?.data?.html}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PrinterIcon className="h-4 w-4 mr-2" />
                Print
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content - Takes remaining space */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-6">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
                <span className="ml-3 text-gray-600">Generating salary slip...</span>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-2xl shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-red-100 rounded-xl flex items-center justify-center">
                    <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-red-800">Error Generating Salary Slip</h3>
                    <p className="text-sm text-red-700 mt-1">Failed to generate salary slip. Please try again.</p>
                  </div>
                </div>
              </div>
            )}

            {data?.data?.payroll && (
              <div className="space-y-8">
                {/* Employee Information */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <UserIcon className="h-4 w-4 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-bold text-blue-900">Employee Information</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <UserIcon className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-700">Name</span>
                      </div>
                      <p className="text-base font-bold text-blue-900">{data.data.payroll.employeeName}</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <UserIcon className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-700">Employee ID</span>
                      </div>
                      <p className="text-base font-bold text-blue-900">{data.data.payroll.employeeId?.employeeId}</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-700">Department</span>
                      </div>
                      <p className="text-base font-bold text-blue-900">{data.data.payroll.department}</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <BriefcaseIcon className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-700">Designation</span>
                      </div>
                      <p className="text-base font-bold text-blue-900">{data.data.payroll.designation}</p>
                    </div>
                  </div>
                </div>

                {/* Attendance Summary */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center">
                      <CalendarDaysIcon className="h-4 w-4 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Attendance Summary</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100 text-center">
                      <p className="text-2xl font-bold text-blue-900">{data.data.payroll.totalWorkingDays}</p>
                      <p className="text-sm text-blue-700">Total Working Days</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100 text-center">
                      <p className="text-2xl font-bold text-green-900">{data.data.payroll.presentDays}</p>
                      <p className="text-sm text-green-700">Present Days</p>
                    </div>
                    <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-4 border border-red-100 text-center">
                      <p className="text-2xl font-bold text-red-900">{data.data.payroll.absentDays}</p>
                      <p className="text-sm text-red-700">Absent Days</p>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-4 border border-yellow-100 text-center">
                      <p className="text-2xl font-bold text-yellow-900">{data.data.payroll.leaveDays}</p>
                      <p className="text-sm text-yellow-700">Leave Days</p>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-4 border border-cyan-100 text-center">
                      <p className="text-2xl font-bold text-cyan-900">{data.data.payroll.holidayDays}</p>
                      <p className="text-sm text-cyan-700">Holiday Days</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100 text-center">
                      <p className="text-2xl font-bold text-emerald-900">{data.data.payroll.paidLeaveDays}</p>
                      <p className="text-sm text-emerald-700">Paid Leave Days</p>
                    </div>
                  </div>
                </div>

                {/* Earnings */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg flex items-center justify-center">
                      <BanknotesIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Earnings</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                      <span className="font-medium text-blue-900">Basic Salary</span>
                      <span className="font-bold text-blue-900">{formatCurrency(data.data.payroll.basicSalary)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                      <span className="font-medium text-purple-900">HRA</span>
                      <span className="font-bold text-purple-900">{formatCurrency(data.data.payroll.hra)}</span>
                    </div>
                    {data.data.payroll.allowances?.map((allowance, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-100">
                        <span className="font-medium text-cyan-900">{allowance.name}</span>
                        <span className="font-bold text-cyan-900">{formatCurrency(allowance.amount)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center p-4 bg-gradient-to-r from-emerald-100 to-green-100 rounded-xl border-2 border-emerald-200 mt-4">
                      <span className="font-bold text-emerald-900 text-lg">Gross Earnings</span>
                      <span className="font-bold text-emerald-900 text-lg">{formatCurrency(data.data.payroll.grossEarnings)}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-gradient-to-br from-red-100 to-rose-100 rounded-lg flex items-center justify-center">
                      <BanknotesIcon className="h-4 w-4 text-red-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Deductions</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-gradient-to-r from-red-50 to-rose-50 rounded-xl border border-red-100">
                      <span className="font-medium text-red-900">PF Employee</span>
                      <span className="font-bold text-red-900">{formatCurrency(data.data.payroll.pfEmployee)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-100">
                      <span className="font-medium text-orange-900">ESI Employee</span>
                      <span className="font-bold text-orange-900">{formatCurrency(data.data.payroll.esiEmployee)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl border border-yellow-100">
                      <span className="font-medium text-yellow-900">Professional Tax</span>
                      <span className="font-bold text-yellow-900">{formatCurrency(data.data.payroll.professionalTax)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-100">
                      <span className="font-medium text-gray-900">TDS</span>
                      <span className="font-bold text-gray-900">{formatCurrency(data.data.payroll.tds)}</span>
                    </div>
                    {data.data.payroll.loanDeduction > 0 && (
                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                        <span className="font-medium text-purple-900">Loan Deduction</span>
                        <span className="font-bold text-purple-900">{formatCurrency(data.data.payroll.loanDeduction)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center p-4 bg-gradient-to-r from-red-100 to-rose-100 rounded-xl border-2 border-red-200 mt-4">
                      <span className="font-bold text-red-900 text-lg">Total Deductions</span>
                      <span className="font-bold text-red-900 text-lg">{formatCurrency(data.data.payroll.totalDeductions)}</span>
                    </div>
                  </div>
                </div>

                {/* Loan Repayment Details */}
                {data.data.payroll.loanDeductionDetails && data.data.payroll.loanDeductionDetails.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center">
                        <DocumentTextIcon className="h-4 w-4 text-purple-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Loan Repayment Details</h3>
                    </div>
                    <div className="space-y-4">
                      {data.data.payroll.loanDeductionDetails.map((detail, index) => (
                        <div key={index} className="flex justify-between items-center p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                          <div className="flex flex-col">
                            <span className="text-sm font-semibold text-purple-900">
                              Loan ID: {detail.loanApplicationId}
                            </span>
                            <span className="text-xs text-purple-700">
                              Installment {detail.installmentNumber}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-bold text-purple-900">{formatCurrency(detail.amount)}</span>
                            <div className="text-xs text-purple-600">
                              Due: {new Date(detail.dueDate).toLocaleDateString('en-GB')}
                            </div>
                            <div className="text-xs text-purple-600">
                              Remaining: {formatCurrency(detail.remainingBalance)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Net Salary */}
                <div className="bg-gradient-to-r from-emerald-500 to-green-600 rounded-2xl p-8 text-center text-white shadow-lg">
                  <div className="flex items-center justify-center space-x-3 mb-4">
                    <CheckCircleIcon className="h-8 w-8" />
                    <h3 className="text-2xl font-bold">Net Salary</h3>
                  </div>
                  <p className="text-4xl font-bold">{formatCurrency(data.data.payroll.netSalary)}</p>
                </div>

                {/* Tax Information */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center">
                      <DocumentTextIcon className="h-4 w-4 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Tax Information</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <DocumentTextIcon className="h-4 w-4 text-indigo-600" />
                        <span className="text-sm font-semibold text-indigo-700">Tax Regime</span>
                      </div>
                      <p className="text-base font-bold text-indigo-900">{data.data.payroll.taxRegime?.toUpperCase()}</p>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-4 border border-cyan-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <BanknotesIcon className="h-4 w-4 text-cyan-600" />
                        <span className="text-sm font-semibold text-cyan-700">Taxable Income</span>
                      </div>
                      <p className="text-base font-bold text-cyan-900">{formatCurrency(data.data.payroll.taxableIncome)}</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <CheckCircleIcon className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-semibold text-green-700">TDS Exemption Applied</span>
                      </div>
                      <p className="text-base font-bold text-green-900">{data.data.payroll.tdsExemptionApplied ? 'Yes' : 'No'}</p>
                    </div>
                    {data.data.payroll.tdsExemptionReason && (
                      <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-4 border border-yellow-100 md:col-span-2">
                        <div className="flex items-center space-x-2 mb-2">
                          <DocumentTextIcon className="h-4 w-4 text-yellow-600" />
                          <span className="text-sm font-semibold text-yellow-700">Exemption Reason</span>
                        </div>
                        <p className="text-base font-bold text-yellow-900">{data.data.payroll.tdsExemptionReason}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bank & Statutory Information */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-100 to-green-100 rounded-lg flex items-center justify-center">
                      <BanknotesIcon className="h-4 w-4 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Bank & Statutory Information</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Bank Details */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <BanknotesIcon className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-700">Bank Name</span>
                      </div>
                      <p className="text-base font-bold text-blue-900">{data.data.payroll.bankName || '-'}</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <DocumentTextIcon className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-semibold text-purple-700">Account Number</span>
                      </div>
                      <p className="text-base font-bold text-purple-900">{data.data.payroll.bankAccountNumber || '-'}</p>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-4 border border-cyan-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <DocumentTextIcon className="h-4 w-4 text-cyan-600" />
                        <span className="text-sm font-semibold text-cyan-700">IFSC Code</span>
                      </div>
                      <p className="text-base font-bold text-cyan-900">{data.data.payroll.ifsc || '-'}</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <UserIcon className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-semibold text-green-700">Account Holder</span>
                      </div>
                      <p className="text-base font-bold text-green-900">{data.data.payroll.accountHolderName || data.data.payroll.employeeName || '-'}</p>
                    </div>
                    
                    {/* Statutory Details */}
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <DocumentTextIcon className="h-4 w-4 text-orange-600" />
                        <span className="text-sm font-semibold text-orange-700">PAN Number</span>
                      </div>
                      <p className="text-base font-bold text-orange-900">{data.data.payroll.pan || '-'}</p>
                    </div>
                    <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-4 border border-red-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <DocumentTextIcon className="h-4 w-4 text-red-600" />
                        <span className="text-sm font-semibold text-red-700">UAN Number</span>
                      </div>
                      <p className="text-base font-bold text-red-900">{data.data.payroll.uanNumber || '-'}</p>
                    </div>
                    <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-4 border border-violet-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <DocumentTextIcon className="h-4 w-4 text-violet-600" />
                        <span className="text-sm font-semibold text-violet-700">ESIC Number</span>
                      </div>
                      <p className="text-base font-bold text-violet-900">{data.data.payroll.esiNumber || '-'}</p>
                    </div>
                    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-100">
                      <div className="flex items-center space-x-2 mb-2">
                        <DocumentTextIcon className="h-4 w-4 text-teal-600" />
                        <span className="text-sm font-semibold text-teal-700">PF Number</span>
                      </div>
                      <p className="text-base font-bold text-teal-900">{data.data.payroll.pfNumber || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer - Fixed height */}
        <div className="flex-shrink-0 p-6 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gradient-to-r from-gray-500 to-slate-600 hover:from-gray-600 hover:to-slate-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalarySlipDialog;
